import Menu from "./ResponsiveMenu";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import {
  AccessTime,
  ArrowDownward,
  ArrowUpward,
  ChildCare,
  ChevronRight,
  Close,
  Forum,
  Groups,
  ImportContacts,
  MoreTime,
  MusicNote,
  Person,
  RecordVoiceOver,
  School,
  Sort,
  Timer,
} from '@mui/icons-material';
import {
  StoryLibraryLength,
  StoryLibraryMode,
  StoryLibraryQuery,
  StoryLibraryReader,
  StoryLibrarySortOrder,
} from '../../api/network_calls';
import {
  DASHBOARD_SCROLLED_GLASS_BACKGROUND,
  DASHBOARD_SCROLLED_GLASS_BORDER,
  DASHBOARD_SCROLLED_GLASS_FILTER,
  DASHBOARD_SCROLLED_GLASS_SHADOW,
} from './dashboardGlass';
import {
  storyActionSheetChevronSx,
  storyActionSheetIconSx,
  storyActionSheetItemSx,
  storyActionSheetPaperSx,
} from './StoryFormControls';
import { LIBRARY_CONTROL_HEIGHT, libraryButtonSizingSx } from './buttonSizing';
import { FORM_CONTROL, formPickerSurfaceSx } from './formControlStyles';

type StoryLibraryControlsProps = {
  query: StoryLibraryQuery;
  disabled?: boolean;
  onChange: (query: StoryLibraryQuery) => void;
};

type ControlKey = 'sort' | 'type' | 'length' | 'reader';

type ControlOption<T extends string> = {
  value: T;
  label: string;
  icon: React.ElementType;
};

const SORT_OPTIONS: Array<ControlOption<StoryLibrarySortOrder>> = [
  { value: 'newest', label: 'Newest first', icon: ArrowDownward },
  { value: 'oldest', label: 'Oldest first', icon: ArrowUpward },
];

const MODE_OPTIONS: Array<ControlOption<StoryLibraryMode>> = [
  { value: 'Regular', label: 'Narrated', icon: RecordVoiceOver },
  { value: 'Dialogue', label: 'Dialogue', icon: Forum },
  { value: 'Musical', label: 'Musical', icon: MusicNote },
];

const LENGTH_OPTIONS: Array<ControlOption<StoryLibraryLength>> = [
  { value: 'Normal', label: 'Normal', icon: AccessTime },
  { value: 'Long', label: 'Long', icon: MoreTime },
];

const READER_OPTIONS: Array<ControlOption<StoryLibraryReader>> = [
  { value: 'Toddler', label: 'Toddler', icon: ChildCare },
  { value: 'Teen', label: 'Teen', icon: School },
  { value: 'Adult', label: 'Adult', icon: Person },
];

const controlButtonSx = {
  ...formPickerSurfaceSx,
  ...libraryButtonSizingSx,
  borderRadius: FORM_CONTROL.pillRadius,
  flex: '0 0 auto',
  color: 'rgba(255,255,255,0.82)',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  fontWeight: 700,
  '& .MuiButton-startIcon': { mr: 0.85 },
  '& .MuiButton-startIcon > :nth-of-type(1)': { fontSize: 20 },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.54)',
    backgroundColor: FORM_CONTROL.surface,
    borderColor: 'rgba(255,255,255,0.12)',
    opacity: 1,
  },
} as const;

const activeControlButtonSx = {
  color: '#FFFFFF',
  borderColor: '#D8A363',
  backgroundColor: FORM_CONTROL.surface,
  '&:hover': {
    borderColor: '#E8B373',
    backgroundColor: FORM_CONTROL.hoverSurface,
  },
} as const;

export const storyLibraryPillButtonSx = (active: boolean) => ({
  ...controlButtonSx,
  ...(active ? activeControlButtonSx : {}),
});

export const storyLibraryActionPillButtonSx = {
  ...controlButtonSx,
  color: '#101219',
  borderColor: '#D8A363',
  backgroundColor: '#D8A363',
  '&:hover': {
    borderColor: '#E8B373',
    backgroundColor: '#E8B373',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.48)',
    backgroundColor: '#1B1E28',
    borderColor: 'rgba(255,255,255,0.12)',
    opacity: 1,
  },
} as const;

export const storyLibraryClearButtonSx = storyLibraryActionPillButtonSx;

export const storyLibraryViewToggleSx = (active: boolean) => ({
  width: LIBRARY_CONTROL_HEIGHT,
  height: LIBRARY_CONTROL_HEIGHT,
  flexShrink: 0,
  borderRadius: '50%',
  color: active ? '#fff' : 'rgba(255,255,255,0.72)',
  bgcolor: '#1B1E28',
  border: `2px solid ${active ? '#D8A363' : 'transparent'}`,
  '&:hover': { bgcolor: '#20232E' },
  '& svg': { fontSize: 20 },
});

export const storyLibraryToolbarSx = {
  width: '100%',
  py: 0.25,
  mb: { xs: 1, sm: 1.75, md: 2 },
} as const;

export const storyLibraryStickyToolbarSx = {
  position: 'sticky',
  top: 'var(--fable-dashboard-modal-top, 0px)',
  zIndex: 1250,
  isolation: 'isolate',
  mx: { xs: -1.5, sm: -2 },
  px: { xs: 1.5, sm: 2 },
  py: { xs: 1.1, sm: 1.35 },
  mb: { xs: 2.25, md: 2.75 },
  background: DASHBOARD_SCROLLED_GLASS_BACKGROUND,
  backdropFilter: DASHBOARD_SCROLLED_GLASS_FILTER,
  WebkitBackdropFilter: DASHBOARD_SCROLLED_GLASS_FILTER,
  borderBottom: DASHBOARD_SCROLLED_GLASS_BORDER,
  boxShadow: DASHBOARD_SCROLLED_GLASS_SHADOW,
} as const;

const StoryLibraryControls: React.FC<StoryLibraryControlsProps> = ({
  query,
  disabled = false,
  onChange,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const controlRefs = useRef<Partial<Record<ControlKey, HTMLButtonElement | null>>>({});
  const lastSelectedControlRef = useRef<ControlKey | null>(null);
  const [activeMenu, setActiveMenu] = useState<ControlKey | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const selectedSort = SORT_OPTIONS.find(option => option.value === query.sortOrder);
  const selectedMode = MODE_OPTIONS.find(option => option.value === query.storyModes[0]);
  const selectedLength = LENGTH_OPTIONS.find(option => option.value === query.storyLengths[0]);
  const selectedReader = READER_OPTIONS.find(option => option.value === query.storyReaders[0]);

  const activeFilterCount = Number(query.sortOrder !== undefined)
    + Number(query.storyModes.length > 0)
    + Number(query.storyLengths.length > 0)
    + Number(query.storyReaders.length > 0);

  const controls = useMemo(() => [
    {
      key: 'sort' as const,
      label: selectedSort?.label ?? 'Sort',
      icon: selectedSort?.icon ?? Sort,
      active: Boolean(selectedSort),
    },
    {
      key: 'type' as const,
      label: selectedMode?.label ?? 'Story Type',
      icon: selectedMode?.icon ?? ImportContacts,
      active: Boolean(selectedMode),
    },
    {
      key: 'length' as const,
      label: selectedLength?.label ?? 'Story Length',
      icon: selectedLength?.icon ?? Timer,
      active: Boolean(selectedLength),
    },
    {
      key: 'reader' as const,
      label: selectedReader?.label ?? 'Age of Reader',
      icon: selectedReader?.icon ?? Groups,
      active: Boolean(selectedReader),
    },
  ], [selectedLength, selectedMode, selectedReader, selectedSort]);

  const menuTitle = activeMenu === 'sort'
    ? 'Sort by'
    : activeMenu === 'type'
      ? 'Story Type'
      : activeMenu === 'length'
        ? 'Story Length'
        : 'Age of Reader';

  const menuOptions: Array<ControlOption<any>> = activeMenu === 'sort'
    ? SORT_OPTIONS
    : activeMenu === 'type'
      ? MODE_OPTIONS
      : activeMenu === 'length'
        ? LENGTH_OPTIONS
        : READER_OPTIONS;

  const selectedMenuValue = activeMenu === 'sort'
    ? query.sortOrder
    : activeMenu === 'type'
      ? query.storyModes[0]
      : activeMenu === 'length'
        ? query.storyLengths[0]
        : query.storyReaders[0];

  const updateOverflow = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    setCanScrollLeft(element.scrollLeft > 8);
    setCanScrollRight(maxScrollLeft > 8 && element.scrollLeft < maxScrollLeft - 8);
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(updateOverflow);
    window.addEventListener('resize', updateOverflow);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateOverflow);
    };
  }, [activeFilterCount, updateOverflow]);

  useEffect(() => {
    const key = lastSelectedControlRef.current;
    if (!key) return;
    const frame = requestAnimationFrame(() => {
      controlRefs.current[key]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
      lastSelectedControlRef.current = null;
    });
    return () => cancelAnimationFrame(frame);
  }, [query.sortOrder, query.storyLengths, query.storyModes, query.storyReaders]);

  const openMenu = useCallback((key: ControlKey, event: React.MouseEvent<HTMLElement>) => {
    setActiveMenu(key);
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setActiveMenu(null);
    setAnchorEl(null);
  }, []);

  const selectOption = useCallback((value: string) => {
    if (!activeMenu) return;
    lastSelectedControlRef.current = activeMenu;
    if (activeMenu === 'sort') {
      onChange({ ...query, sortOrder: value as StoryLibrarySortOrder });
    } else if (activeMenu === 'type') {
      onChange({ ...query, storyModes: [value as StoryLibraryMode] });
    } else if (activeMenu === 'length') {
      onChange({ ...query, storyLengths: [value as StoryLibraryLength] });
    } else {
      onChange({ ...query, storyReaders: [value as StoryLibraryReader] });
    }
    closeMenu();
  }, [activeMenu, closeMenu, onChange, query]);

  const clearCurrentMenu = useCallback(() => {
    if (activeMenu === 'sort') {
      onChange({ ...query, sortOrder: undefined });
    } else if (activeMenu === 'type') {
      onChange({ ...query, storyModes: [] });
    } else if (activeMenu === 'length') {
      onChange({ ...query, storyLengths: [] });
    } else if (activeMenu === 'reader') {
      onChange({ ...query, storyReaders: [] });
    }
    closeMenu();
  }, [activeMenu, closeMenu, onChange, query]);

  const clearAll = useCallback(() => {
    lastSelectedControlRef.current = null;
    scrollRef.current?.scrollTo({ left: 0, behavior: 'auto' });
    onChange({ storyModes: [], storyLengths: [], storyReaders: [] });
  }, [onChange]);

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      <Box
        ref={scrollRef}
        onScroll={updateOverflow}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          overflowX: 'auto',
          py: 0.25,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {controls.map(control => {
          const Icon = control.icon;
          return (
            <Button
              key={control.key}
              ref={node => { controlRefs.current[control.key] = node; }}
              variant="outlined"
              disabled={disabled}
              startIcon={<Icon sx={{ fontSize: 21 }} />}
              onClick={event => openMenu(control.key, event)}
              aria-pressed={control.active}
              sx={storyLibraryPillButtonSx(control.active)}
            >
              {control.label}
            </Button>
          );
        })}

        {activeFilterCount > 0 && (
          <Button
            variant="contained"
            disabled={disabled}
            startIcon={<Close sx={{ fontSize: 21 }} />}
            onClick={clearAll}
            sx={storyLibraryClearButtonSx}
          >
            {activeFilterCount > 1 ? 'Clear Filters' : 'Clear Filter'}
          </Button>
        )}
      </Box>

      {canScrollLeft && (
        <Box
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            zIndex: 2,
            left: 0,
            top: 0,
            bottom: 0,
            width: 32,
            background: 'linear-gradient(90deg, #101219 10%, rgba(16,18,25,0))',
          }}
        />
      )}
      {canScrollRight && (
        <Box
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            zIndex: 2,
            right: 0,
            top: 0,
            bottom: 0,
            width: 32,
            background: 'linear-gradient(270deg, #101219 10%, rgba(16,18,25,0))',
          }}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(activeMenu && anchorEl)}
        onClose={closeMenu}
        PaperProps={{ sx: storyActionSheetPaperSx }}
        MenuListProps={{ disablePadding: true, sx: { pb: { xs: 1.5, sm: 1 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2.5, sm: 1.8 },
            pt: { xs: 2, sm: 1.5 },
            pb: { xs: 1.25, sm: 1 },
          }}
        >
          <Typography sx={{ fontSize: { xs: 22, sm: 18 }, fontWeight: 800, color: '#fff' }}>
            {menuTitle}
          </Typography>
          {selectedMenuValue !== undefined && (
            <Button
              variant="contained"
              startIcon={<Close />}
              onClick={clearCurrentMenu}
              sx={{
                minHeight: { xs: 38, sm: 32 },
                borderRadius: FORM_CONTROL.pillRadius,
                px: { xs: 1.5, sm: 1.1 },
                fontSize: { sm: 13 },
                color: '#000',
                backgroundColor: '#D8A363',
                textTransform: 'none',
                fontWeight: 800,
                '&:hover': { backgroundColor: '#E8B373' },
              }}
            >
              Clear
            </Button>
          )}
        </Box>
        {menuOptions.map(option => {
          const OptionIcon = option.icon;
          const selected = selectedMenuValue === option.value;
          return (
            <MenuItem
              key={option.value}
              selected={selected}
              onClick={() => selectOption(option.value)}
              sx={{
                ...storyActionSheetItemSx,
                ...(selected
                  ? {
                      color: '#fff',
                      borderWidth: 2,
                      borderColor: '#76A9E7',
                      '&.Mui-selected': { backgroundColor: '#1B1E28' },
                      '&.Mui-selected:hover': { backgroundColor: '#20232E' },
                    }
                  : {}),
              }}
            >
              <Box
                component="span"
                sx={{
                  ...storyActionSheetIconSx,
                  bgcolor: '#252A36',
                  color: selected ? '#76A9E7' : '#E0E0E0',
                }}
              >
                <OptionIcon />
              </Box>
              <Typography
                component="span"
                sx={{ flex: 1, minWidth: 0, fontSize: 'inherit', fontWeight: 'inherit' }}
              >
                {option.label}
              </Typography>
              <ChevronRight
                sx={{
                  ...storyActionSheetChevronSx,
                  color: selected ? '#76A9E7' : '#B9BAC5',
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default StoryLibraryControls;
